import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  DEFAULT_DOCK_WIDTH,
  DOCK_MAX_WIDTH,
  DOCK_MIN_WIDTH,
  useAppStore,
} from '../../store/appStore';
import { darkTheme } from '../../theme/tokens';

const t = darkTheme;

// Keyboard nudge step for the resize handle (px per arrow press).
const RESIZE_KEY_STEP = 16;

interface ChatResizeHandleProps {
  /** The X coordinate (viewport px) of the chat surface's RIGHT edge — a drag maps
   *  cursor X → width as `right − clientX` since the surface grows leftward. The
   *  fixed dock is flush to the viewport (`() => window.innerWidth`); the in-flow
   *  `ChatPanel` measures its own `getBoundingClientRect().right`. Read at drag time
   *  (not once) so a live layout shift doesn't stale the mapping. */
  measureRightEdge: () => number;
}

/**
 * The drag-to-resize splitter shared by the chat surfaces (SC-2393, extracted at
 * P3/SC-2659). A 6px hit area straddling the surface's left border with a hairline
 * that brightens to accent on hover/focus; owns the pointer drag, the keyboard
 * nudge (←/→/Home/End), the double-click reset, and the mid-drag-unmount teardown.
 * Absolutely positioned, so the host surface must be `position: relative`/`fixed`.
 * Width lives in `appStore` (`dockWidth`, clamped by the setter); the only geometry
 * that differs between hosts is `measureRightEdge`.
 */
export function ChatResizeHandle({ measureRightEdge }: ChatResizeHandleProps) {
  const dockWidth = useAppStore((s) => s.dockWidth);
  const setDockWidth = useAppStore((s) => s.setDockWidth);
  const setDockResizing = useAppStore((s) => s.setDockResizing);
  const [handleActive, setHandleActive] = useState(false);
  // Holds the active drag's teardown so an unmount mid-drag can run it. The drag
  // sets `dockResizing` (a shared-store flag that outlives this component) and
  // attaches window listeners + body styles before the terminating pointerup — so
  // if the handle unmounts first (dock closed / panel collapsed / navigated away),
  // they must be cleaned up explicitly (mirrors the tableViewStore isPaging rule).
  const endResizeRef = useRef<(() => void) | null>(null);

  const startResize = (e: ReactPointerEvent) => {
    e.preventDefault();
    setDockResizing(true);
    const onMove = (ev: PointerEvent) => setDockWidth(measureRightEdge() - ev.clientX);
    const endResize = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endResize);
      // pointercancel fires when the browser takes over the pointer (touch scroll,
      // system dialog, orientation change) and no pointerup follows — without
      // removing/handling it the drag would never tear down.
      window.removeEventListener('pointercancel', endResize);
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
      setDockResizing(false);
      endResizeRef.current = null;
    };
    endResizeRef.current = endResize;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endResize);
    window.addEventListener('pointercancel', endResize);
  };

  // Tear down an in-progress drag if the handle unmounts before pointerup.
  useEffect(() => () => endResizeRef.current?.(), []);

  // Keyboard a11y: ←/→ nudge (← widens since the surface grows leftward), Home/End
  // jump to the bounds, and double-click resets to the default width.
  const onResizeKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowLeft') setDockWidth(dockWidth + RESIZE_KEY_STEP);
    else if (e.key === 'ArrowRight') setDockWidth(dockWidth - RESIZE_KEY_STEP);
    else if (e.key === 'Home') setDockWidth(Infinity); // clamps to max
    else if (e.key === 'End') setDockWidth(0); // clamps to min
    else return;
    e.preventDefault();
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize chat panel"
      // A focusable separator is a splitter — announce the current width + bounds.
      aria-valuenow={dockWidth}
      aria-valuemin={DOCK_MIN_WIDTH}
      aria-valuemax={DOCK_MAX_WIDTH}
      tabIndex={0}
      onPointerDown={startResize}
      onDoubleClick={() => setDockWidth(DEFAULT_DOCK_WIDTH)}
      onKeyDown={onResizeKey}
      onMouseEnter={() => setHandleActive(true)}
      onMouseLeave={() => setHandleActive(false)}
      onFocus={() => setHandleActive(true)}
      onBlur={() => setHandleActive(false)}
      style={{
        position: 'absolute',
        left: -3,
        top: 0,
        bottom: 0,
        width: 6,
        cursor: 'col-resize',
        touchAction: 'none',
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 2,
          height: '100%',
          background: handleActive ? t.accent : 'transparent',
          transition: 'background 0.12s',
        }}
      />
    </div>
  );
}
